import type { PrescriptionImage } from '@/entities/performance/api/performance.api';
import { PrescriptionLineCard } from '@/entities/performance/components/PrescriptionLineCard';
import { useImageDrugs } from '@/entities/performance/hooks/usePerformanceQueries';
import { useRefreshableImageUrl } from '@/entities/performance/hooks/useRefreshableImageUrl';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/utils';
import { X } from 'lucide-react';
import { useEffect, useState, type KeyboardEvent } from 'react';
import { ImageStatusBadge } from './PerformanceParts';

const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;

const zoomBtn =
  'flex h-7 items-center justify-center rounded-md bg-white/18 text-xs leading-[18px] font-medium text-white hover:bg-white/30 disabled:opacity-40';

/**
 * M-18 처방 이미지 뷰어 (1360×956, 모바일 전체 화면)
 * - 좌우 버튼 · ←/→ 키로 이미지를 넘기면 오른쪽 처방 실적도 그 이미지 것으로 바뀐다
 * - 확대 25~400% (25 단위), 맞춤 = 100% · 회전 0°, 회전은 90°씩
 * - 이미지 인덱스는 컴포넌트 상태로만 둔다 (새로고침 복원 없음)
 * - 약명을 누르면 뷰어를 닫고 드로어로 바꾼다 (M-15 B "모달 위에는 쌓지 않음")
 */
export const ImageViewerDialog = ({
  images,
  index,
  onIndexChange,
  onClose,
  ym,
  clinicId,
  clinicName,
}: {
  images: PrescriptionImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  ym: string;
  clinicId: number;
  clinicName: string;
}) => {
  const image = images[index];
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  // 선택 정산월에 속한 연결 실적만 온다
  const lines = useImageDrugs(ym, clinicId, image?.imageCode);
  const { src, onError } = useRefreshableImageUrl(ym, clinicId, image);

  // 이미지를 넘기면 확대 · 회전을 초기화한다
  useEffect(() => {
    setZoom(100);
    setRotation(0);
  }, [index]);

  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  const ymLabel = `${ym.slice(0, 4)}년 ${ym.slice(4, 6)}월`;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' && hasPrev) {
      e.preventDefault();
      onIndexChange(index - 1);
    }
    if (e.key === 'ArrowRight' && hasNext) {
      e.preventDefault();
      onIndexChange(index + 1);
    }
  };

  const items = lines.data?.content ?? [];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideClose
        onKeyDown={onKeyDown}
        // 약명을 눌러 드로어로 바꿀 때 포커스가 썸네일로 돌아가면 드로어가 "바깥 포커스"로 닫힌다
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="overflow-y-auto lg:h-[956px] lg:max-h-[calc(100vh-32px)] lg:max-w-[1360px] lg:overflow-hidden"
      >
        {/* 헤더 */}
        <header className="flex shrink-0 items-center gap-3 border-b border-line py-4 pr-4 pl-4 lg:py-[18px] lg:pr-5 lg:pl-7">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <DialogTitle className="text-base leading-[27px] lg:text-lg">
                처방 이미지 뷰어
              </DialogTitle>
              {image && <ImageStatusBadge image={image} />}
            </div>
            <DialogDescription className="truncate text-xs leading-[19px] text-[#8d8d8d] lg:text-[12.5px]">
              {clinicName} · {ymLabel} · {image?.displayName}
            </DialogDescription>
          </div>
          <span className="ml-auto shrink-0 rounded-md bg-muted px-3 py-1.5 text-[12.5px] leading-[19px] font-medium text-ink-2">
            {index + 1} / {images.length}
          </span>
          <DialogClose
            aria-label="닫기"
            className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-ink-2 hover:bg-[#e8e8e8]"
          >
            <X className="size-4" />
          </DialogClose>
        </header>

        <div className="flex flex-col lg:min-h-0 lg:flex-1 lg:flex-row">
          {/* 좌측 이미지 스테이지 560 */}
          <div className="relative flex h-[60vh] shrink-0 items-center justify-center overflow-hidden bg-[#1a1a1a] lg:h-auto lg:w-[560px]">
            {image && !src && (
              <p className="text-[13px] leading-5 text-[#8d8d8d]">
                원본 이미지를 불러올 수 없어요.
              </p>
            )}
            {image && src && (
              <img
                src={src}
                onError={onError}
                alt={`처방 이미지 ${image.displayName}`}
                draggable={false}
                className="max-h-[calc(100%-120px)] max-w-[calc(100%-80px)] rounded-[6px] object-contain transition-transform duration-150 select-none lg:max-h-[580px] lg:max-w-[480px]"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                }}
              />
            )}
            <button
              type="button"
              aria-label="이전 이미지"
              disabled={!hasPrev}
              onClick={() => onIndexChange(index - 1)}
              className="absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-base leading-6 font-medium text-ink disabled:opacity-30 lg:left-5"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label="다음 이미지"
              disabled={!hasNext}
              onClick={() => onIndexChange(index + 1)}
              className="absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-base leading-6 font-medium text-ink disabled:opacity-30 lg:right-5"
            >
              ›
            </button>
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-sm bg-black/62 px-2 py-1.5 lg:bottom-[49px]">
              <button
                type="button"
                aria-label="축소"
                className={cn(zoomBtn, 'w-[30px]')}
                disabled={zoom <= ZOOM_MIN}
                onClick={() =>
                  setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))
                }
              >
                −
              </button>
              <span className="w-11 text-center text-xs leading-[18px] font-medium text-white">
                {zoom}%
              </span>
              <button
                type="button"
                aria-label="확대"
                className={cn(zoomBtn, 'w-[30px]')}
                disabled={zoom >= ZOOM_MAX}
                onClick={() =>
                  setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))
                }
              >
                +
              </button>
              <button
                type="button"
                className={cn(zoomBtn, 'w-10')}
                onClick={() => {
                  setZoom(100);
                  setRotation(0);
                }}
              >
                맞춤
              </button>
              <button
                type="button"
                className={cn(zoomBtn, 'w-10')}
                onClick={() => setRotation((r) => (r + 90) % 360)}
              >
                회전
              </button>
            </div>
          </div>

          {/* 우측 연결된 처방 실적 */}
          <section className="flex min-w-0 flex-1 flex-col px-4 py-5 lg:overflow-y-auto lg:px-6 lg:py-[22px]">
            <div className="flex items-center gap-2">
              <h3 className="text-[15px] leading-[23px] font-bold text-ink">
                연결된 처방 실적
              </h3>
              {lines.data && (
                <span className="rounded-badge bg-primary-soft px-2 py-[3px] text-[11px] leading-[17px] font-medium text-primary">
                  {lines.data.totalCount}품목
                </span>
              )}
            </div>
            <p className="mt-1.5 text-[11.5px] leading-[17px] text-[#8d8d8d]">
              이 이미지에 연결된 처방 중 {ymLabel} 정산분입니다 · {clinicName}
            </p>
            <div className="mt-4">
              {lines.isPending ? (
                <LoadingBlock className="h-[143px]" />
              ) : lines.isError ? (
                <ErrorState onRetry={() => void lines.refetch()} />
              ) : items.length === 0 ? (
                <EmptyState message="아직 이 이미지에서 확인된 처방 내역이 없어요." />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {items.map((line) => (
                    <PrescriptionLineCard
                      key={line.prescriptionId}
                      line={line}
                      clinicName={clinicName}
                      variant="compact"
                      onDrugOpen={onClose}
                    />
                  ))}
                </div>
              )}
              {/* TODO(server): 이미지 1장 기준 합계(예상 · 확정)가 없어 합계 행을 두지 않았다 (docs/api B8) */}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
